'use client';

import { faGhost } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tooltip } from '@pickleballinc/react-ui';
import { trim } from 'lodash';
import { Fragment, useRef, useState } from 'react';

import { StandingsTablesData } from '@/modules/league/details/standings/ClubRating';
import { formatValueForTables } from '@/modules/league/details/standings/formatValueForTables';
import { getConnectDUPRURL } from '@/utils/helpers/getConnectDUPRURL';

export default function Factors({ session, players, user }: StandingsTablesData) {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollContainerRef.current) return;

    setIsDragging(true);
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
    scrollContainerRef.current.style.cursor = 'grabbing';
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollContainerRef.current) return;
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = x - startX;
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    if (!scrollContainerRef.current) return;

    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    if (!scrollContainerRef.current) return;
    setIsDragging(false);
  };
  return (
    <>
      <div
        className='mt-2 select-text overflow-auto md:ml-4'
        ref={scrollContainerRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      >
        <div className='vertical-header-cell  inline-flex h-28  w-auto flex-nowrap rounded-t-lg border-x border-t bg-white font-semibold text-gray-900 lg:flex'>
          <div className='inline-flex w-12 shrink-0 items-center px-2 py-3 text-sm font-medium text-gray-900 [transform:rotate(180deg)] [writing-mode:vertical-lr]'>
            Rank
          </div>
          <div className='inline-flex w-72 shrink-0   px-2 py-3 text-sm font-medium' />
          <div className='inline-flex w-14 shrink-0  items-center px-2 py-3 text-sm font-medium [transform:rotate(180deg)] [writing-mode:vertical-lr]'>
            Previous
          </div>
          {parseFloat(session?.MaxMovementRule) * 100 > 0 &&
          session?.OnStandings_HideMaxMovement === false ? (
            <div className='inline-flex w-14 shrink-0   items-center px-2 py-3 text-sm font-medium [transform:rotate(180deg)] [writing-mode:vertical-lr]'>
              Max Mvmt
            </div>
          ) : null}
          {parseFloat(session?.Group1_MatchDateBonus) > 0 ||
          parseFloat(session?.Group2_MatchDateBonus) > 0 ||
          parseFloat(session?.Group3_MatchDateBonus) > 0 ||
          parseFloat(session?.Group4_MatchDateBonus) > 0 ||
          parseFloat(session?.Group5_MatchDateBonus) > 0 ||
          parseFloat(session?.Group6_MatchDateBonus) > 0 ||
          parseFloat(session?.Group7_MatchDateBonus) > 0 ||
          parseFloat(session?.Group8_MatchDateBonus) > 0 ||
          parseFloat(session?.Group9_MatchDateBonus) > 0 ||
          parseFloat(session?.Group10_MatchDateBonus) > 0 ? (
            <div className='inline-flex w-14 shrink-0   items-center px-2 py-3 text-sm font-medium [transform:rotate(180deg)] [writing-mode:vertical-lr]'>
              Group Bonus
            </div>
          ) : null}
          {session?.OnStandings_HideRatingOrEarnedToPossiblePercentage === false ? (
            <div className='inline-flex w-24 shrink-0   items-center px-2 py-3 text-sm font-medium [transform:rotate(180deg)] [writing-mode:vertical-lr]'>
              E/P Diff %
            </div>
          ) : null}
          {session?.OnStandings_HidePenalty === false ? (
            <div className='inline-flex w-24 shrink-0  items-center px-2 py-3 text-sm font-medium [transform:rotate(180deg)] [writing-mode:vertical-lr]'>
              Penalty{' '}
            </div>
          ) : null}
          {session?.OnStandings_HideRatingOrEarnedToPossiblePercentageMinusPenalty === false ? (
            <div className='inline-flex w-24 shrink-0   items-center px-2 py-3 text-sm font-medium [transform:rotate(180deg)] [writing-mode:vertical-rl]'>
              E/P Diff % - Penalty
            </div>
          ) : null}
          {session?.OnStandings_HideEarned === false ? (
            <div className='inline-flex w-14 shrink-0  items-center px-2 py-3 text-sm font-medium [transform:rotate(180deg)] [writing-mode:vertical-lr]'>
              Earned{' '}
            </div>
          ) : null}
          {session?.OnStandings_HideAgainst === false ? (
            <div className='inline-flex w-24 shrink-0  items-center px-2 py-3 text-sm font-medium [transform:rotate(180deg)] [writing-mode:vertical-lr]'>
              Against{' '}
            </div>
          ) : null}
          {session?.OnStandings_HidePossible === false ? (
            <div className='inline-flex w-14 shrink-0   items-center px-2 py-3 text-sm font-medium [transform:rotate(180deg)] [writing-mode:vertical-lr]'>
              Possible
            </div>
          ) : null}
          <div className='inline-flex w-14 shrink-0  items-center px-2 py-3 text-sm font-medium [transform:rotate(180deg)] [writing-mode:vertical-lr]'>
            Wins
          </div>
          <div className='inline-flex w-14 shrink-0   items-center px-2 py-3 text-sm font-medium [transform:rotate(180deg)] [writing-mode:vertical-lr]'>
            Losses
          </div>
          {session?.OnStandings_HideNumberWeeksPlayed === false ? (
            <div className='inline-flex w-14 shrink-0  items-center px-2 py-3 text-sm font-medium [transform:rotate(180deg)] [writing-mode:vertical-lr]'>
              # Played
            </div>
          ) : null}
          {session?.OnStandings_HideNumberWeeksMissed === false ? (
            <div className='inline-flex w-14 shrink-0  items-center px-2 py-3 text-sm font-medium [transform:rotate(180deg)] [writing-mode:vertical-lr]'>
              # Missed
            </div>
          ) : null}
          {session?.OnStandings_HideMissCurrentWeek === false ? (
            <div className='inline-flex w-14 shrink-0  items-center px-2 py-3 text-sm font-medium [transform:rotate(180deg)] [writing-mode:vertical-rl]'>
              Miss Cur Game Day{' '}
            </div>
          ) : null}
        </div>
        <div className='mb-4 inline-flex w-auto flex-col flex-nowrap  border'>
          {players?.map((player, i) => {
            return (
              <Fragment key={`player-row-${i}`}>
                <div
                  className={` flex w-full border-y border-gray-300  ${
                    i === 0
                      ? 'bg-moss-300'
                      : i === 1
                        ? `mt-1 bg-moss-200`
                        : i === 2
                          ? `mt-1 bg-moss-100`
                          : i === 3
                            ? `mt-1 bg-moss-50`
                            : i === 4
                              ? `mt-1 bg-moss-25`
                              : `mt-1 bg-gray-50`
                  }`}
                  key={i}
                >
                  <div className='inline-flex w-12 shrink-0 justify-center break-words border-r px-2 py-4 text-sm font-semibold'>
                    <span className='self-center'>{player.CurrentSeedNumber}</span>
                  </div>
                  <div className='w-72 shrink-0 flex-row break-words border-r px-2 py-4 text-sm font-semibold '>
                    <div>
                      {player.FirstName_1} {player.LastName_1}
                      {session.CountGamesTowardRatings && !player.DUPR1Active ? (
                        <span className='text-xs font-medium text-gray-600'>
                          {user?.pbUuid &&
                          player.UserID_1.toLowerCase() === user?.pbUuid.toLowerCase() ? (
                            <a
                              className='text-primary-500 underline'
                              href={getConnectDUPRURL(
                                player.UserID_1,
                                'l',
                                window.location.href,
                                window.location.href
                              )}
                              aria-label='Link to connect to DUPR'
                            >
                              Connect To DUPR
                            </a>
                          ) : (
                            '(Not Linked to DUPR)'
                          )}
                        </span>
                      ) : null}
                    </div>
                    {player.FirstName_2 !== '' && player.LastName_2 !== '' ? (
                      <div>
                        {player.FirstName_2} {player.LastName_2}
                        {session.CountGamesTowardRatings && !player.DUPR2Active ? (
                          <span className='text-xs font-medium text-gray-600'>
                            {user?.pbUuid &&
                            player.UserID_2 &&
                            player.UserID_2.toLowerCase() === user?.pbUuid.toLowerCase() ? (
                              <a
                                className='text-primary-500 underline'
                                href={getConnectDUPRURL(
                                  player.UserID_2,
                                  'l',
                                  window.location.href,
                                  window.location.href
                                )}
                                aria-label='Link to connect to DUPR'
                              >
                                Connect To DUPR
                              </a>
                            ) : (
                              '(Not Linked to DUPR)'
                            )}
                          </span>
                        ) : null}
                        {trim(player.AttendeeSessionID_2).length === 0 ? (
                          <Tooltip placement='top-start'>
                            <Tooltip.Trigger asChild>
                              <FontAwesomeIcon icon={faGhost} className='ml-2  block' />
                            </Tooltip.Trigger>
                            <Tooltip.Content className='w-42 rounded bg-black bg-black/75 p-2 text-sm text-white'>
                              Not Registered
                            </Tooltip.Content>
                          </Tooltip>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                  <div className='inline-flex w-14 shrink-0 justify-center break-words border-r px-2 py-4 text-sm '>
                    <span className='self-center'>{player.PrevSeedNumber}</span>
                  </div>{' '}
                  {parseFloat(session?.MaxMovementRule) * 100 > 0 &&
                  session?.OnStandings_HideMaxMovement === false ? (
                    <div className='inline-flex w-14 shrink-0 justify-center break-words border-r px-2 py-4 text-sm '>
                      <span className='self-center'>
                        {player.DidUseMaxMovementRule === true ? 'Y' : 'N'}
                        {player.WouldHaveUsedUseMaxMovementRule === true &&
                        player.DidUseMaxMovementRule === false ? (
                          <span className=' text-red-600 font-semibold'>*</span>
                        ) : null}
                      </span>
                    </div>
                  ) : null}
                  {parseFloat(session?.Group1_MatchDateBonus) > 0 ||
                  parseFloat(session?.Group2_MatchDateBonus) > 0 ||
                  parseFloat(session?.Group3_MatchDateBonus) > 0 ||
                  parseFloat(session?.Group4_MatchDateBonus) > 0 ||
                  parseFloat(session?.Group5_MatchDateBonus) > 0 ||
                  parseFloat(session?.Group6_MatchDateBonus) > 0 ||
                  parseFloat(session?.Group7_MatchDateBonus) > 0 ||
                  parseFloat(session?.Group8_MatchDateBonus) > 0 ||
                  parseFloat(session?.Group9_MatchDateBonus) > 0 ||
                  parseFloat(session?.Group10_MatchDateBonus) > 0 ? (
                    <div className='inline-flex w-14 shrink-0 justify-center break-words border-r px-2 py-4 text-sm '>
                      <span className='self-center'>
                        {parseFloat(player.Group_MatchDateBonus || '0').toFixed(3)}
                      </span>
                    </div>
                  ) : null}
                  {/* <%
                                     If ShowGrupBonus Then
                                         %>
                                        <div class="block text-center b-r p-t-5 p-b-5 b-t b-b flex flex-middle flex-center" style="background-color:<%=bgColor%> !important; width: 65px; min-width: 65px; max-width: 65px;">
                                           <%=Group_MatchDateBonus %>
                                        </div>
                                <% End if %> //TODO */}
                  {session?.OnStandings_HideRatingOrEarnedToPossiblePercentage === false ? (
                    <div className='inline-flex w-24 shrink-0 justify-center break-words border-r px-2 py-4 text-sm '>
                      <span className='self-center'>
                        {parseFloat(player.TotalPoints_Difference_EarnedPossible_Percentage) *
                          100 <=
                        0.0
                          ? '0'
                          : formatValueForTables(
                              player.TotalPoints_Difference_EarnedPossible_Percentage,
                              100
                            )}
                        %
                      </span>
                    </div>
                  ) : null}
                  {session?.OnStandings_HidePenalty === false ? (
                    <div className='inline-flex w-24 shrink-0 justify-center break-words border-r px-2 py-4 text-sm '>
                      <span className='self-center'>
                        {formatValueForTables(player.TotalMatchDate_Penalty) === '10.0000'
                          ? '0'
                          : formatValueForTables(player.TotalMatchDate_Penalty)}
                      </span>
                    </div>
                  ) : null}
                  {session?.OnStandings_HideRatingOrEarnedToPossiblePercentageMinusPenalty ===
                  false ? (
                    <div className='inline-flex w-24 shrink-0 justify-center break-words border-r px-2 py-4 text-sm '>
                      <span className='self-center'>
                        {parseFloat(
                          player.TotalPoints_Difference_EarnedPossible_Percentage_AfterPenalties
                        ) *
                          100 <
                        0
                          ? '0'
                          : formatValueForTables(
                              player.TotalPoints_Difference_EarnedPossible_Percentage_AfterPenalties,
                              100
                            )}
                        %
                      </span>
                    </div>
                  ) : null}
                  {session?.OnStandings_HideEarned === false ? (
                    <div className='inline-flex w-14 shrink-0 justify-center break-words border-r px-2 py-4 text-sm '>
                      <span className='self-center'>{player.TotalPoints_Earned}</span>
                    </div>
                  ) : null}
                  {session?.OnStandings_HideAgainst === false ? (
                    <div className='inline-flex w-24 shrink-0 justify-center break-words border-r px-2 py-4 text-sm '>
                      <span className='self-center'>{player.TotalPoints_Against}</span>
                    </div>
                  ) : null}
                  {session?.OnStandings_HidePossible === false ? (
                    <div className='inline-flex w-14 shrink-0 justify-center break-words border-r px-2 py-4 text-sm '>
                      <span className='self-center'>{player.TotalPoints_Possible}</span>
                    </div>
                  ) : null}
                  <div className='inline-flex w-14 shrink-0 justify-center break-words border-r px-2 py-4 text-sm '>
                    <span className='self-center'>{player.TotalWins}</span>
                  </div>
                  <div className='inline-flex w-14 shrink-0 justify-center break-words border-r px-2 py-4 text-sm '>
                    <span className='self-center'>{player.TotalLosses}</span>
                  </div>
                  {session?.OnStandings_HideNumberWeeksPlayed === false ? (
                    <div className='inline-flex w-14 shrink-0 justify-center break-words border-r px-2 py-4 text-sm '>
                      <span className='self-center'>{player.TotalMatchDate_Played}</span>
                    </div>
                  ) : null}
                  {session?.OnStandings_HideNumberWeeksMissed === false ? (
                    <div className='inline-flex w-14 shrink-0 justify-center break-words border-r px-2 py-4 text-sm '>
                      <span className='self-center'>{player.TotalMatchDate_Missed}</span>
                    </div>
                  ) : null}
                  {session?.OnStandings_HideMissCurrentWeek === false ? (
                    <div className='inline-flex w-14 shrink-0 justify-center break-words px-2 py-4 text-sm '>
                      <span className='self-center'>
                        {player.DidMissCurrentMatchDate === true ? 'Y' : 'N'}
                      </span>
                    </div>
                  ) : null}
                </div>
              </Fragment>
            );
          })}
        </div>
      </div>
      <div className='my-4 w-full rounded-lg bg-gray-100 p-4 text-left text-sm text-gray-900 md:text-base'>
        <div>
          <div className='mb-1'>
            This league has a{' '}
            <span className='font-semibold'>{parseFloat(session?.MaxMovementRule) * 100}%</span> Max
            Movement Rule.
          </div>
          <div className='mb-6'>
            Max Movement per player this week is{' '}
            <span className='font-semibold'>{session?.MaxMovementFromCurrentWeek}</span>.
          </div>
          <div>
            <span className=' text-red-600'>* </span>
            This player would have hit their Max Movement although other players, because of their
            Max Movement, prevented them from moving anymore{' '}
            <span className='font-semibold'>OR </span>
            they did not play the current week. Absent players CANNOT move up.
          </div>{' '}
        </div>
      </div>
    </>
  );
}
